import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal, Spinner } from '../../components/ui';
import { useToast } from '../../components/ui/toast';
import {
  postsApi,
  type CampaignInsights,
  type InsightMetrics,
  type PostInsights,
  type PostInsightsDetail,
  type ScheduledPost,
} from '../../lib/api';
import i18n from '../../i18n';
import { errorMessage } from '../contacts/shared';
import { formatCompact, formatNumber } from '../../lib/format';
import { PLATFORM_NAMES, platformOf } from './limits';

/**
 * Instagram's numbers for a post (D-115): the notices under a published post,
 * the detail modal with the day-by-day history and what the automations did,
 * and a campaign's totals. Real numbers only: before the first sync the UI
 * says when they arrive, never shows zeros. The list's columns are in
 * `postRows.tsx` (refonte, D-128).
 */

// A fixed locale: "1.2K" reads the same everywhere and keeps tests deterministic.
const COMPACT = { format: (value: number) => formatCompact(value) };
export const compactNumber = (n: number): string => COMPACT.format(n);

/** "il y a 4 min", "4 min ago": how long since the last sync, in the reader's language. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return i18n.t('ago.now');
  if (minutes < 60) return i18n.t('ago.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return i18n.t('ago.hours', { count: hours });
  const days = Math.round(hours / 24);
  return days === 1 ? i18n.t('ago.yesterday') : i18n.t('ago.days', { count: days });
}

type MetricKey = Exclude<keyof InsightMetrics & keyof CampaignInsights, 'totalInteractions'>;

/** A campaign's totals, labelled like the posts list's columns. */
const METRICS: ReadonlyArray<MetricKey> = ['views', 'reach', 'likes', 'comments', 'saved', 'shares'];

/** The modal's tiles; a story's comments are its replies (D-136). */
type ModalMetric = 'views' | 'reach' | 'likes' | 'comments' | 'saved' | 'shares';
const MODAL_METRICS: ReadonlyArray<ModalMetric> = ['views', 'reach', 'likes', 'comments', 'saved', 'shares'];
const columnOf = (key: ModalMetric, type: ScheduledPost['type']): ModalMetric | 'replies' => (key === 'comments' && type === 'story' ? 'replies' : key);

const RECONNECT_CODES: ReadonlySet<string> = new Set(['INSIGHTS_SCOPE_MISSING', 'ACCOUNT_NOT_CONNECTED']);
type InsightsCode = 'INSIGHTS_SCOPE_MISSING' | 'ACCOUNT_NOT_CONNECTED' | 'INSIGHTS_UNAVAILABLE' | 'MEDIA_GONE' | 'SYNC_FAILED';
const KNOWN_CODES: ReadonlySet<string> = new Set<InsightsCode>(['INSIGHTS_SCOPE_MISSING', 'ACCOUNT_NOT_CONNECTED', 'INSIGHTS_UNAVAILABLE', 'MEDIA_GONE', 'SYNC_FAILED']);

/**
 * Why some numbers are missing, with Reconnect when a permission is what's
 * missing. A known code is said in the reader's language (Q8); anything else
 * keeps the server's words.
 */
export function InsightsNotice({ error, post }: { error: NonNullable<PostInsights['error']>; post: ScheduledPost }) {
  const { t } = useTranslation();
  const text = KNOWN_CODES.has(error.code)
    ? t(`insights.errors.${error.code as InsightsCode}`, {
        account: post.channelAccount.displayName,
        platform: PLATFORM_NAMES[platformOf(post.channelAccount.channelCode)],
      })
    : error.message;
  return (
    <p className="mt-0.5 text-xs text-(--color-caution-text)">
      {text}
      {RECONNECT_CODES.has(error.code) ? (
        <>
          {' '}
          <Link to="/settings/channels" className="font-semibold underline">
            {t('insights.reconnect')}
          </Link>
        </>
      ) : null}
    </p>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-(--radius-control) bg-(--color-surface-sunken) px-3 py-2.5">
      <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-(--color-ink-500)">{label}</dt>
      <dd className="tabular mt-1 text-lg font-semibold text-(--color-ink-900)">{value}</dd>
    </div>
  );
}

const valueOf = (n: number | null): string => (n === null ? '—' : compactNumber(n));

/** The full numbers, the automations' side, and the day-by-day history, with a refresh. */
export function PostInsightsModal({ post, onClose }: { post: ScheduledPost; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const detail = useQuery({ queryKey: ['post-insights', post.id], queryFn: () => postsApi.insights(post.id) });
  const refresh = useMutation({
    mutationFn: () => postsApi.refreshInsights(post.id),
    onSuccess: async (fresh) => {
      queryClient.setQueryData<PostInsightsDetail>(['post-insights', post.id], (current) => (current ? { ...current, ...fresh } : current));
      await queryClient.invalidateQueries({ queryKey: ['post-insights', post.id] });
      await queryClient.invalidateQueries({ queryKey: ['posts'] });
      await queryClient.invalidateQueries({ queryKey: ['campaign'] });
      toast(
        fresh.error
          ? { title: t('insights.partlyRefreshed'), body: fresh.error.message, tone: 'error' }
          : { title: t('insights.refreshed'), tone: 'success' },
      );
    },
    onError: (err) => toast({ title: t('insights.refreshFailed'), body: errorMessage(err), tone: 'error' }),
  });
  const data = detail.data;
  const isStory = post.type === 'story';
  const watchTime = (ms: number) => t('insights.seconds', { value: formatNumber(Math.round(ms / 100) / 10) });

  return (
    <Modal title={t(`posts.kind.${isStory ? 'story' : 'post'}.insights`)} onClose={onClose}>
      {detail.isError ? (
        <Alert>{errorMessage(detail.error)}</Alert>
      ) : !data ? (
        <Spinner label={t('insights.loading')} />
      ) : (
        <div className="space-y-5">
          {data.metrics ? (
            <dl className="grid grid-cols-3 gap-3">
              {MODAL_METRICS.map((key) => (
                <Tile key={key} label={t(`posts.columns.${columnOf(key, post.type)}`)} value={valueOf(data.metrics![key])} />
              ))}
              {post.type === 'reel' && data.metrics.avgWatchTimeMs !== null ? (
                <Tile label={t('insights.avgWatchTime')} value={watchTime(data.metrics.avgWatchTimeMs)} />
              ) : null}
            </dl>
          ) : data.error ? null : (
            <p className="text-sm text-(--color-ink-500)">{t('insights.pending')}</p>
          )}
          {data.error ? <InsightsNotice error={data.error} post={post} /> : null}

          {/* A story has no comments, so nothing answers them: that section would only ever read zero. */}
          {isStory ? null : (
            <section aria-labelledby="insights-automation">
              <h3 id="insights-automation" className="text-sm font-semibold text-(--color-ink-900)">
                {t('insights.automationsTitle')}
              </h3>
              <dl className="tabular mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm text-(--color-ink-700)">
                <dt>{t('insights.commentsReceived')}</dt>
                <dd className="font-semibold text-(--color-ink-900)">{data.automation.comments}</dd>
                <dt>{t('insights.triggered')}</dt>
                <dd className="font-semibold text-(--color-ink-900)">{data.automation.triggered}</dd>
                <dt>{t('insights.messagesSent')}</dt>
                <dd className="font-semibold text-(--color-ink-900)">{data.automation.messagesSent}</dd>
                <dt>{t('insights.contactsReached')}</dt>
                <dd className="font-semibold text-(--color-ink-900)">{data.automation.contactsReached}</dd>
              </dl>
            </section>
          )}

          {data.history.length > 1 ? (
            <section aria-labelledby="insights-history">
              <h3 id="insights-history" className="text-sm font-semibold text-(--color-ink-900)">
                {t('insights.dayByDay')}
              </h3>
              <table className="tabular mt-2 w-full text-left text-sm">
                <thead className="text-[11px] font-bold uppercase tracking-[0.08em] text-(--color-ink-500)">
                  <tr>
                    <th className="py-1 font-bold">{t('insights.day')}</th>
                    <th className="py-1 text-right font-bold">{t('posts.columns.views')}</th>
                    <th className="py-1 text-right font-bold">{t('posts.columns.reach')}</th>
                    <th className="py-1 text-right font-bold">{t('posts.columns.likes')}</th>
                    <th className="py-1 text-right font-bold">{t(`posts.columns.${columnOf('comments', post.type)}`)}</th>
                  </tr>
                </thead>
                <tbody className="text-(--color-ink-700)">
                  {data.history.slice(0, 14).map((point) => (
                    <tr key={point.day} className="border-t border-(--color-line)">
                      <td className="py-1">{point.day}</td>
                      <td className="py-1 text-right">{valueOf(point.views)}</td>
                      <td className="py-1 text-right">{valueOf(point.reach)}</td>
                      <td className="py-1 text-right">{valueOf(point.likes)}</td>
                      <td className="py-1 text-right">{valueOf(point.comments)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-(--color-ink-500)">
              {data.syncedAt ? t('insights.syncedAgo', { ago: timeAgo(data.syncedAt) }) : t('insights.notSynced')}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                {t('insights.close')}
              </Button>
              <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
                {t('insights.refresh')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** A campaign's totals over its published posts; nothing until one is published. */
export function CampaignInsightsPanel({ insights, publishedCount }: { insights: CampaignInsights; publishedCount: number }) {
  if (publishedCount === 0) return null;
  if (insights.posts === 0) return <p className="text-xs text-(--color-ink-500)">{i18n.t('insights.pending')}</p>;
  return (
    <div>
      {/* Two columns on a phone: "COMMENTAIRES" does not fit a third of it. */}
      <dl className="grid grid-cols-2 gap-3 @min-[480px]:grid-cols-3 @min-[760px]:grid-cols-6">
        {METRICS.map((key) => (
          <Tile key={key} label={i18n.t(`posts.columns.${key}`)} value={valueOf(insights[key])} />
        ))}
      </dl>
      <p className="mt-2 text-xs text-(--color-ink-500)">
        {i18n.t('insights.campaignAcross', { count: insights.posts })}
      </p>
    </div>
  );
}
